/**
 * AI Agent 채팅 클라이언트 (ADR 0026 · Issue #18 · Phase A)
 * --------------------------------------------------------------------------
 * `POST /api/ai/chat` 하나만 씁니다. **브라우저는 Holmes를 모릅니다** — 주소도,
 * 이벤트 이름도, 토큰도 이 계층에 존재하지 않습니다.
 *
 * `EventSource`를 쓰지 않는 이유는 POST와 헤더가 필요하기 때문입니다. 대신
 * fetch + ReadableStream으로 같은 SSE 규약을 읽고, 끊기면 마지막 이벤트 id를
 * `Last-Event-ID`로 실어 **같은 턴에 다시 붙습니다**(ADR 0007과 같은 패턴).
 *
 * 세 가지가 유계입니다 — 프레임 하나의 바이트, 버퍼 전체, 그리고 재접속 횟수.
 * 어느 하나를 넘으면 조용히 멈추지 않고 사유가 있는 오류로 끝냅니다.
 */
import {
  AI_CHAT_LIMITS,
  isAIChatErrorCode,
  isAIChatEventType,
  isSafeResourceRef,
  safeActions,
  safeApprovalRefs,
  safeProgressLabel,
  type AIActionProposal,
  type AIActionApprovalRef,
  type AIChatErrorCode,
  type AIChatEvent,
  type AIChatRequest,
  type AIFinalEvent,
  type AIProgressStep,
  type AIResourceRef,
} from "@k8s-dashboard/contracts";
import { getManagerToken, getSessionCSRF } from "./client";

export const AI_CHAT_PATH = "/api/ai/chat";

/** 프레임 하나와 버퍼 전체의 상한. 서버 상한보다 넉넉하되 무한하지 않습니다. */
const MAX_FRAME_BYTES = 128 * 1024;
const MAX_BUFFER_BYTES = 512 * 1024;
/** 재접속 시도 상한. 넘으면 사용자가 직접 다시 물어봅니다. */
export const MAX_RESUMES = 3;

export interface AIChatHandlers {
  onSession?(session: { sessionId: string; turnId: string }): void;
  onProgress?(progress: { step: AIProgressStep; label: string }): void;
  onMessage?(delta: string): void;
  onFinal?(final: AIFinalEvent): void;
  onError?(code: AIChatErrorCode, retryAfterSeconds?: number): void;
}

/** 스트림이 끝난 이유. 화면은 이 값으로 재시도 버튼을 정합니다. */
export type AIChatOutcome = "final" | "error" | "aborted";

/**
 * 한 번의 질문을 보내고 스트림을 소비합니다.
 *
 * `signal`이 취소되면 요청과 함께 upstream까지 취소가 전파됩니다(서버가 유예 뒤
 * Holmes를 끊습니다). 중간에 연결이 끊기면 마지막 id로 **같은 턴**에 다시 붙으므로
 * 사용자는 답변을 잃지 않고, 서버는 새 LLM 호출을 만들지 않습니다.
 */
export async function streamAIChat(
  request: AIChatRequest,
  handlers: AIChatHandlers,
  signal?: AbortSignal,
): Promise<AIChatOutcome> {
  let lastEventId = "";
  let resumes = 0;
  for (;;) {
    let outcome: AIChatOutcome | "reconnect";
    try {
      outcome = await runOnce(request, handlers, lastEventId, (id) => (lastEventId = id), signal);
    } catch (error) {
      if (signal?.aborted) return "aborted";
      outcome = "reconnect";
      void error;
    }
    if (outcome !== "reconnect") return outcome;
    if (signal?.aborted) return "aborted";
    if (lastEventId === "" || resumes >= MAX_RESUMES) {
      handlers.onError?.("ai_upstream_unavailable");
      return "error";
    }
    resumes += 1;
  }
}

async function runOnce(
  request: AIChatRequest,
  handlers: AIChatHandlers,
  lastEventId: string,
  remember: (id: string) => void,
  signal?: AbortSignal,
): Promise<AIChatOutcome | "reconnect"> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    accept: "text/event-stream",
  };
  const csrf = getSessionCSRF();
  if (csrf) headers["X-CSRF-Token"] = csrf;
  const managerToken = getManagerToken();
  if (managerToken) headers.Authorization = `Bearer ${managerToken}`;
  if (lastEventId) headers["Last-Event-ID"] = lastEventId;

  const response = await fetch(new URL(AI_CHAT_PATH, window.location.origin), {
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok || !response.body) {
    const code = await readErrorCode(response);
    const retryAfter = Number(response.headers.get("Retry-After") ?? "");
    handlers.onError?.(code, Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined);
    await response.body?.cancel().catch(() => undefined);
    return "error";
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let outcome: AIChatOutcome | "reconnect" = "reconnect";
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      if (buffer.length > MAX_BUFFER_BYTES) {
        handlers.onError?.("ai_upstream_unavailable");
        return "error";
      }
      let split = buffer.indexOf("\n\n");
      while (split >= 0) {
        const raw = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        const result = consumeFrame(raw, handlers, remember);
        if (result) outcome = result;
        if (outcome === "final" || outcome === "error") return outcome;
        split = buffer.indexOf("\n\n");
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined);
  }
  return outcome;
}

/** 프레임 하나를 소비합니다. 계약 밖의 것은 값이 아니라 무시입니다. */
function consumeFrame(
  raw: string,
  handlers: AIChatHandlers,
  remember: (id: string) => void,
): AIChatOutcome | "reconnect" | undefined {
  if (raw.length > MAX_FRAME_BYTES) return undefined;
  let id = "";
  let event = "";
  let data = "";
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) continue;
    if (line.startsWith("id: ")) id = line.slice(4).trim();
    else if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) data += line.slice(6);
  }
  if (!isAIChatEventType(event) || data === "") return undefined;
  const parsed = parseEvent(event, data);
  if (!parsed) return undefined;
  /* 재개 지점은 **계약을 통과한 프레임**의 id만입니다.

     파싱 전에 기억하면, 중간 프록시나 미래의 서버 버전이 보낸 모르는 이벤트
     하나가 재개 지점을 앞으로 밀어냅니다. 그 뒤 연결이 끊기면 그 사이의 진짜
     프레임은 서버가 이미 보냈다고 판단해 다시 보내지 않고, 사용자는 답변의
     일부를 영원히 잃습니다. */
  if (id) remember(id);
  switch (parsed.type) {
    case "session":
      handlers.onSession?.(parsed.data);
      return undefined;
    case "progress":
      handlers.onProgress?.({ step: parsed.data.step, label: safeProgressLabel(parsed.data.label) });
      return undefined;
    case "message":
      handlers.onMessage?.(parsed.data.delta);
      return undefined;
    case "final":
      handlers.onFinal?.(parsed.data);
      return "final";
    case "error":
      handlers.onError?.(parsed.data.code, parsed.data.retryAfterSeconds);
      return "error";
  }
}

/**
 * SSE payload를 계약 모양으로만 받아들입니다.
 *
 * 서버가 이미 닫힌 집합으로 정규화하지만 화면도 한 번 더 봅니다 — 중간 프록시가
 * 바꿔치기한 프레임이나 미래의 서버 버전이 렌더 경로에 그대로 들어오면 안 됩니다.
 */
function parseEvent(type: string, data: string): AIChatEvent | undefined {
  let value: unknown;
  try {
    value = JSON.parse(data);
  } catch {
    return undefined;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  switch (type) {
    case "session":
      if (typeof record.sessionId !== "string" || typeof record.turnId !== "string") return undefined;
      return { type: "session", data: { sessionId: record.sessionId, turnId: record.turnId } };
    case "progress": {
      const step = record.step;
      if (step !== "reading" && step !== "thinking" && step !== "answering") return undefined;
      return { type: "progress", data: { step, label: safeProgressLabel(record.label) } };
    }
    case "message":
      if (typeof record.delta !== "string") return undefined;
      return { type: "message", data: { delta: record.delta.slice(0, AI_CHAT_LIMITS.answerBytes) } };
    case "final": {
      if (typeof record.messageId !== "string" || typeof record.content !== "string") return undefined;
      /* 제안은 서버가 이미 계약으로 검증하고 이 턴의 권한·근거에 결합했습니다.
         그래도 화면이 한 번 더 봅니다 — 중간 프록시나 미래의 서버 버전이 보낸
         제안이 카드 렌더 경로에 그대로 들어오면 안 됩니다. 통과하지 못한 제안은
         카드가 아니라 **사라지고**, 답변 본문은 그대로 남습니다. (ADR 0027) */
      const actions = safeAgentActions(record.actions);
      /* 승인 참조도 같은 규칙으로 다시 봅니다. (ADR 0028 Phase C)

         서버가 주지 않았거나 계약 밖이면 **빈 목록**이고, 그때 카드는 Phase B 그대로
         제안 전용입니다 — 승인 컨트롤이 없는 것이 안전한 기본값입니다. */
      const approvals = safeApprovalRefs(record.approvals);
      return {
        type: "final",
        data: {
          messageId: record.messageId,
          content: record.content.slice(0, AI_CHAT_LIMITS.answerBytes),
          references: safeReferences(record.references),
          truncated: record.truncated === true,
          ...(actions.length > 0 ? { actions } : {}),
          ...(approvals.length > 0 ? { approvals } : {}),
        },
      };
    }
    case "error": {
      if (!isAIChatErrorCode(record.code)) return undefined;
      const retry = record.retryAfterSeconds;
      return {
        type: "error",
        data: {
          code: record.code,
          retryAfterSeconds: typeof retry === "number" && retry > 0 ? retry : undefined,
        },
      };
    }
    default:
      return undefined;
  }
}

/** 안전한 참조만 남깁니다. 통과하지 못한 참조는 링크가 아니라 **사라집니다**. */
export function safeReferences(value: unknown): AIResourceRef[] {
  if (!Array.isArray(value)) return [];
  const out: AIResourceRef[] = [];
  for (const item of value) {
    if (out.length >= AI_CHAT_LIMITS.references) break;
    if (isSafeResourceRef(item)) out.push(item);
  }
  return out;
}

/**
 * 계약을 통과한 제안만 남깁니다. (ADR 0027 Phase B)
 *
 * 근거가 실제로 성공한 조회인지, 대상이 이 턴의 권한 안인지는 서버만 알 수 있고
 * 서버가 이미 판정했습니다. 화면은 **모양**을 독립적으로 다시 봅니다 — 이 계층이
 * 서버를 신뢰의 근거로 삼으면, 서버 앞에 무엇이 있든 카드가 그려집니다.
 */
export function safeAgentActions(value: unknown): AIActionProposal[] {
  return safeActions(value);
}

/** 승인 참조의 화면 쪽 재검증. 계약 밖이면 빈 목록이고 카드는 제안 전용으로 남습니다. */
export function safeAgentApprovals(value: unknown): AIActionApprovalRef[] {
  return safeApprovalRefs(value);
}

async function readErrorCode(response: Response): Promise<AIChatErrorCode> {
  try {
    const body = (await response.clone().json()) as { reasonCode?: unknown; code?: unknown };
    if (isAIChatErrorCode(body.reasonCode)) return body.reasonCode;
    if (isAIChatErrorCode(body.code)) return body.code;
  } catch {
    /* 본문이 계약이 아니면 상태 코드로 판단합니다. */
  }
  if (response.status === 429) return "ai_rate_limited";
  if (response.status === 503) return "ai_disabled";
  if (response.status === 404) return "ai_session_not_found";
  if (response.status === 400) return "ai_invalid_request";
  return "ai_upstream_unavailable";
}
